export type TodoAiSuggestionProviderKind = 'openai' | 'mock' | 'local-mock';

export interface TodoAiSuggestionItemInput {
    title: string;
    completed: boolean;
    kind: 'todo' | 'routine';
    category: string;
    memo: string;
    repeatLabel: string;
    reminderLabel: string;
    timerLabel: string;
}

export interface TodoAiSuggestionContext {
    date: string;
    dateLabel: string;
    userName: string;
    representativeEmoji: string;
    diaryMemo: string;
    completedCount: number;
    openCount: number;
    totalCount: number;
    completionRate: number;
    totalTimerLabel: string;
    todos: TodoAiSuggestionItemInput[];
}

export interface TodoAiSuggestionResult {
    provider: TodoAiSuggestionProviderKind;
    providerLabel: string;
    diaryDraft: string;
    tomorrowTodos: string[];
    createdAt: string;
    warning?: string;
}

export abstract class AiSuggestionProvider {
    public abstract generate(context: TodoAiSuggestionContext): Promise<TodoAiSuggestionResult>;
}

declare const wiz: any;

export class DashboardApiAiSuggestionProvider extends AiSuggestionProvider {
    public async generate(context: TodoAiSuggestionContext): Promise<TodoAiSuggestionResult> {
        const response = await wiz.call('generate_ai_suggestions', {
            context: JSON.stringify(context)
        });
        if (!response || Number(response.code) >= 400) {
            const message = response?.data?.message || response?.message || 'AI 초안을 만들지 못했습니다.';
            throw new Error(message);
        }
        return normalizeAiSuggestionResult(response.data?.result || response.data?.data || response.data);
    }
}

export class MockAiSuggestionProvider extends AiSuggestionProvider {
    public async generate(context: TodoAiSuggestionContext): Promise<TodoAiSuggestionResult> {
        return createMockAiSuggestionResult(context, 'local-mock');
    }
}

export function createAiSuggestionProvider(): AiSuggestionProvider {
    if (typeof wiz !== 'undefined' && typeof wiz.call === 'function') {
        return new DashboardApiAiSuggestionProvider();
    }
    return new MockAiSuggestionProvider();
}

export function hasTodoAiSuggestionInput(context: TodoAiSuggestionContext): boolean {
    return context.todos.length > 0
        || Boolean((context.diaryMemo || '').trim())
        || Boolean((context.representativeEmoji || '').trim().replace('☁', ''));
}

export function createMockAiSuggestionResult(
    context: TodoAiSuggestionContext,
    provider: TodoAiSuggestionProviderKind = 'mock'
): TodoAiSuggestionResult {
    const completed = context.todos.filter((item) => item.completed);
    const open = context.todos.filter((item) => !item.completed);
    const completedTitles = completed.slice(0, 3).map((item) => item.title).filter(Boolean);
    const openTitles = open.slice(0, 2).map((item) => item.title).filter(Boolean);
    const focusTitle = completedTitles[0] || context.diaryMemo || '오늘의 루틴';
    const emoji = context.representativeEmoji || '☁';
    const progressText = context.totalCount > 0
        ? `${context.completedCount}/${context.totalCount}개를 마쳤고 완료율은 ${context.completionRate}%였습니다.`
        : '오늘은 아직 Todo가 많지 않았지만 흐름을 정리해 보았습니다.';
    const completedText = completedTitles.length > 0
        ? `특히 ${completedTitles.join(', ')}을 끝낸 점이 기억에 남습니다.`
        : '작은 정리와 기록으로 하루의 기준을 세운 점이 좋았습니다.';
    const openText = openTitles.length > 0
        ? `남은 ${openTitles.join(', ')}은 내일 부담 없는 순서로 이어가면 좋겠습니다.`
        : '남은 일은 크지 않아 내일은 새 계획을 가볍게 시작해도 좋겠습니다.';

    const recommendations = [
        openTitles[0] ? `${openTitles[0]} 이어서 정리하기` : `${focusTitle} 후속 정리하기`,
        '내일 첫 Todo 1개만 먼저 체크하기',
        context.diaryMemo ? '오늘 메모에서 이어갈 일 10분 정리하기' : '하루 시작 전 우선순위 3개 고르기'
    ];

    return {
        provider,
        providerLabel: provider === 'local-mock' ? 'Local mock provider' : 'Mock provider',
        diaryDraft: `${emoji} ${context.dateLabel} 회고\n${progressText}\n${completedText}\n${openText}`,
        tomorrowTodos: uniqueNonEmptyLines(recommendations).slice(0, 4),
        createdAt: new Date().toISOString()
    };
}

function normalizeAiSuggestionResult(data: any): TodoAiSuggestionResult {
    const fallback = createMockAiSuggestionResult({
        date: '',
        dateLabel: '선택 날짜',
        userName: '',
        representativeEmoji: '☁',
        diaryMemo: '',
        completedCount: 0,
        openCount: 0,
        totalCount: 0,
        completionRate: 0,
        totalTimerLabel: '0분',
        todos: []
    });
    return {
        provider: normalizeProviderKind(data?.provider) || fallback.provider,
        providerLabel: String(data?.providerLabel || data?.provider_label || fallback.providerLabel),
        diaryDraft: String(data?.diaryDraft || data?.diary_draft || fallback.diaryDraft),
        tomorrowTodos: uniqueNonEmptyLines(data?.tomorrowTodos || data?.tomorrow_todos || fallback.tomorrowTodos).slice(0, 6),
        createdAt: String(data?.createdAt || data?.created_at || new Date().toISOString()),
        warning: data?.warning ? String(data.warning) : undefined
    };
}

function normalizeProviderKind(value: any): TodoAiSuggestionProviderKind | null {
    if (value === 'openai' || value === 'mock' || value === 'local-mock') return value;
    return null;
}

function uniqueNonEmptyLines(value: any): string[] {
    const items = Array.isArray(value) ? value : String(value || '').split('\n');
    const seen = new Set<string>();
    const result: string[] = [];
    for (const item of items) {
        const title = String(item || '').replace(/^[-*]\s+/, '').trim();
        if (!title || seen.has(title)) continue;
        seen.add(title);
        result.push(title);
    }
    return result;
}
